// Frontend-Fehlerüberwachung: JS- und Vue-Fehler gehen über das Sentry-SDK an
// Bugsink, siehe docs/fehlerueberwachung.md.
//
//   import { initErrorTracking } from '@shared/error-tracking';
//   const app = createApp(...);
//   initErrorTracking({ app });   // vor app.mount()
//
// Aufgerufen an jedem createApp() und ohne App in den Haupt-Entries der
// Themes (globale Fehler, Stimulus). Die Konfiguration kommt aus
// <meta name="brunex-error-tracking"> (templates/error_tracking/_meta.html.twig),
// das nur mit SENTRY_FRONTEND_DSN gerendert wird. Ohne Meta-Tag passiert
// nichts: kein SDK, kein errorHandler, Vue verhält sich wie bisher.
//
// Mit Konfiguration lädt die erste Stelle das SDK als eigenen Chunk nach.
// Der Zustand liegt an window, weil mehrere Builds (Theme und
// Lieferzeitenrechner) diesen Code je einmal enthalten. Fehler, die vor dem
// Laden des SDK auftreten, werden gepuffert und danach gemeldet.

const META_NAME = 'brunex-error-tracking';
const STATE_KEY = '__brunexErrorTracking';
const MAX_PENDING = 20;

export function initErrorTracking({ app } = {}) {
  const tracker = getTracker();
  if (tracker && app) {
    tracker.attach(app);
  }
}

function getTracker() {
  if (typeof window === 'undefined') {
    return null;
  }
  if (window[STATE_KEY]) {
    return window[STATE_KEY];
  }

  const config = readConfig();
  if (!config) {
    return null;
  }

  window[STATE_KEY] = createTracker(config);

  return window[STATE_KEY];
}

function readConfig() {
  const content = document.querySelector(`meta[name="${META_NAME}"]`)?.getAttribute('content');
  if (!content) {
    return null;
  }

  try {
    const config = JSON.parse(content);

    return config && typeof config.dsn === 'string' && config.dsn !== '' ? config : null;
  } catch {
    return null;
  }
}

function createTracker(config) {
  const pending = [];
  const attachedApps = new WeakSet();
  let sdk = null;

  const capture = (error, hint) => {
    if (sdk) {
      sdk.captureException(error, hint);
    } else if (pending.length < MAX_PENDING) {
      pending.push([error, hint]);
    }
  };

  // Puffer bis das SDK geladen ist; danach übernimmt dessen GlobalHandlers.
  const onError = (event) => {
    if (event.error || event.message) {
      capture(event.error || event.message, { mechanism: { type: 'auto.browser.global_handlers.onerror', handled: false } });
    }
  };
  const onRejection = (event) => {
    capture(event.reason, { mechanism: { type: 'auto.browser.global_handlers.onunhandledrejection', handled: false } });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  const stopBuffering = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };

  import(/* webpackChunkName: "error-tracking" */ './sentry.js')
    .then(({ startSentry }) => {
      sdk = startSentry(config);
      stopBuffering();
      pending.splice(0).forEach(([error, hint]) => sdk.captureException(error, hint));
    })
    .catch(() => {
      // Die Fehlerüberwachung darf die Seite nie stören (Chunk blockiert o. ä.).
      stopBuffering();
      pending.length = 0;
    });

  return {
    attach(app) {
      if (attachedApps.has(app)) {
        return;
      }
      attachedApps.add(app);

      // Eigener Handler statt Sentry.attachErrorHandler: er muss schon vor
      // dem Laden des SDK greifen, und Props (Kunden-, Adress-, Benutzerdaten)
      // gehen nie mit, nur Komponentenname und Lifecycle-Hook.
      const originalHandler = app.config.errorHandler;
      app.config.errorHandler = (error, instance, info) => {
        capture(error, {
          mechanism: { type: 'auto.function.vue.error_handler', handled: Boolean(originalHandler) },
          captureContext: { contexts: { vue: { componentName: componentName(instance), lifecycleHook: info } } },
        });

        if (originalHandler) {
          originalHandler(error, instance, info);
        } else {
          // Wie Vue ohne errorHandler im Produktions-Build
          console.error(error);
        }
      };
    },
  };
}

function componentName(instance) {
  const options = instance?.$options;
  const file = options?.__file ? options.__file.split(/[\\/]/).pop() : null;

  return options?.name || options?.__name || file || (instance ? 'Anonymous' : 'Root');
}
